import React, {useContext, useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';

import API, {ApiRoutes, catchHandler} from '@api';
import {Court} from '@store/CourtStore';
import {ModalContext} from '@context/ModalContext';
import useStore from '@hook/useStore';
import useKeyboardShortcut from '@hook/useKeyboardShortcut';
import useAbortController from '@hook/useAbortController';

import TextInput from '@component/input/TextInput';
import Button from '@component/button/Button';
import LoadingCircle from '@component/loading/LoadingCircle';
import Textarea from '../input/Textarea';
import FieldLengths from '../../FieldLengths';
import {getPatchObject} from '../../util';

type CourtFormProps = {
  court?: Court;
  onSuccess: (court: Court) => void;
}

const CourtForm = observer(({ court, onSuccess } : CourtFormProps) => {
  const { apiStore, modalStore } = useStore();
  const [busy, setBusy] = useState<boolean>(false);
  const [formData, setFormData] = useState<Court>(court ?? {});
  const [checkInputs, setCheckInputs] = useState<boolean>(null);
  const modalContext = useContext(ModalContext);
  const abortController = useAbortController();

  useEffect(() => {
    setFormData(court ?? {});
  }, [court]);

  useEffect(() => {
    setBusy(apiStore.checkBusy(ApiRoutes.CLIENTS));
  }, [apiStore.busyRoutes.length]);

  const changeHandler = (e: React.FormEvent<HTMLInputElement>) => {
    setFormData({
      ... formData,
      [e.currentTarget.name]: e.currentTarget.value
    });
  }

  const submitHandler = () => {
    setCheckInputs(true);
    setTimeout(() => setCheckInputs(false), 50);

    if (!formData.name || formData.name.length <= 0) {
      document.getElementById('crt_name')?.focus();
      return;
    }

    const apiMethod = court ?  API.patchCourt : API.postCourt;

    // TODO Temporary while we look into if court types are needed
    const courtData: Court = Object.assign({}, formData);
    delete courtData.courtType;

    const apiObject = court ? getPatchObject(court, courtData) : courtData;

    apiMethod(apiObject, abortController.getNewSignal())
      .then(response => {
        modalStore.removeModal(modalContext.data.uuid);
        onSuccess(response.data);
      })
      .catch(catchHandler);
  }

  useKeyboardShortcut('Enter', submitHandler, [formData]);

  const submitText = court ? 'Save Details' : 'Add Court';

  return (
    <div className='form'
         role='form'
         data-busy={ busy }>
      <div className='flex flex-row'>
        <div className='half'>
          <TextInput
            id='crt_name'
            name='name'
            value={ formData.name ?? '' }
            maxLength={ FieldLengths.GENERAL }
            label='Court Name'
            onChange={ changeHandler }
            autoFocus={ true }
            required={ true }
            forceCheckValid={ checkInputs }
          />
          <TextInput
            name='addressLine1'
            value={ formData.addressLine1 ?? '' }
            maxLength={ FieldLengths.GENERAL }
            label='Address Line 1'
            onChange={ changeHandler }
          />
          <TextInput
            name='addressLine2'
            value={ formData.addressLine2 ?? '' }
            maxLength={ FieldLengths.GENERAL }
            label='Address Line 2'
            onChange={ changeHandler }
          />
          <TextInput
            name='town'
            value={ formData.town ?? '' }
            maxLength={ FieldLengths.GENERAL }
            label='Town/City'
            onChange={ changeHandler }
          />
          <TextInput
            name='county'
            value={ formData.county ?? '' }
            maxLength={ FieldLengths.GENERAL }
            label='Country'
            onChange={ changeHandler }
          />
          <TextInput
            name='country'
            value={ formData.country ?? '' }
            maxLength={ FieldLengths.COUNTRY }
            label='Country'
            onChange={ changeHandler }
          />
          <TextInput
            name='postcode'
            value={ formData.postcode ?? '' }
            maxLength={ FieldLengths.POSTCODE }
            label='Postcode'
            onChange={ changeHandler }
          />
        </div>
        <div className='half'>
          <TextInput
            name='contactEmail'
            value={ formData.contactEmail ?? '' }
            maxLength={ FieldLengths.GENERAL }
            label='Contact Email'
            onChange={ changeHandler }
          />
          <TextInput
            name='telephoneNumber'
            value={ formData.telephoneNumber ?? '' }
            maxLength={ FieldLengths.TELEPHONE_NUMBER }
            label='Telephone Number'
            onChange={ changeHandler }
          />
          <TextInput
            name='fax'
            value={ formData.fax ?? '' }
            maxLength={ FieldLengths.FAX }
            label='Fax Number'
            onChange={ changeHandler }
          />
          <TextInput
            name='dxNumber'
            value={ formData.dxNumber ?? '' }
            maxLength={ FieldLengths.DX_NUMBER }
            label='DX Number'
            onChange={ changeHandler }
          />
          <TextInput
            name='laaAccountNumber'
            value={ formData.laaAccountNumber ?? '' }
            maxLength={ FieldLengths.LAA_ACCOUNT_NUMBER }
            label='LAA Account Number'
            onChange={ changeHandler }
          />
        </div>
      </div>
      <Textarea
        name='notes'
        label='Notes'
        value={ formData.notes ?? ''}
        onChange={ changeHandler }
      />
      <div className='button-row flex flex-row flex-center'>
        <Button
          onClick={ submitHandler }
          label={ submitText }>
          { submitText }
        </Button>
      </div>
      {
        busy &&
        <LoadingCircle />
      }
    </div>
  )
});

export default CourtForm;
